// src/services/rankingService.ts - ランキング機能のFirebase操作

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  updateDoc,
  addDoc,
  increment
} from 'firebase/firestore';
import { db } from './firebase';
import { getCurrentUserId } from './auth';

const BAD_THRESHOLD = 5; // バッドがこの数を超えると除外

// ユーザーIDを取得するヘルパー関数
const getUserId = (): string => {
  return getCurrentUserId();
};

// ========== 型定義 ==========

export interface RankingSettings {
  isEnabled: boolean;
  userName: string;
  region: string; // 地域を追加
}

export interface RankingRecord {
  id?: string;
  userId: string;
  userName: string;
  region: string; // 地域を追加
  date: string;
  revenue: number;
  deliveryCount: number;
  workingMinutes: number;
  platform: string;
  createdAt: number;
  goodCount: number;
  badCount: number;
  isExcluded: boolean;
}

export interface Vote {
  userId: string;
  recordId: string;
  type: 'good' | 'bad';
  createdAt: number;
}

export type RankingPeriod = 'today' | 'week' | 'month';

export interface RankingEntry extends RankingRecord {
  rank: number;
  hourlyRate: number;
  isCurrentUser: boolean;
}

// ========== ランキング設定 ==========

export const getRankingSettings = async (): Promise<RankingSettings> => {
  try {
    const userId = getUserId();
    const docRef = doc(db, `users/${userId}/settings`, 'ranking');
    const snapshot = await getDoc(docRef);
    
    if (snapshot.exists()) {
      return snapshot.data() as RankingSettings;
    }
    
    return { isEnabled: false, userName: '', region: '' };
  } catch (error) {
    console.error('ランキング設定取得エラー:', error);
    return { isEnabled: false, userName: '', region: '' };
  }
};

export const setRankingSettings = async (settings: RankingSettings): Promise<void> => {
  try {
    const userId = getUserId();
    const docRef = doc(db, `users/${userId}/settings`, 'ranking');
    await setDoc(docRef, settings, { merge: true });
  } catch (error) {
    console.error('ランキング設定保存エラー:', error);
    throw error;
  }
};

// ========== ランキングレコード ==========

export const submitRankingRecord = async (record: Omit<RankingRecord, 'id' | 'userId' | 'goodCount' | 'badCount' | 'isExcluded' | 'region'>): Promise<void> => {
  try {
    const userId = getUserId();
    const settings = await getRankingSettings();
    
    if (!settings.isEnabled) {
      throw new Error('ランキング機能が有効になっていません');
    }
    
    // 既存の同じ日・同じプラットフォームの記録を検索
    const existingQuery = query(
      collection(db, 'rankings'),
      where('userId', '==', userId),
      where('date', '==', record.date),
      where('platform', '==', record.platform)
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    
    const rankingData: Omit<RankingRecord, 'id'> = {
      ...record,
      userId,
      userName: settings.userName,
      region: settings.region,
      goodCount: 0,
      badCount: 0,
      isExcluded: false
    };
    
    if (!existingSnapshot.empty) {
      // 既存レコードを更新（投票数は保持）
      const existingDoc = existingSnapshot.docs[0];
      const existingData = existingDoc.data() as RankingRecord;
      await updateDoc(doc(db, 'rankings', existingDoc.id), {
        ...rankingData,
        goodCount: existingData.goodCount,
        badCount: existingData.badCount,
        isExcluded: existingData.isExcluded
      });
    } else {
      // 新規作成
      await addDoc(collection(db, 'rankings'), rankingData);
    }
  } catch (error) {
    console.error('ランキング記録送信エラー:', error);
    throw error;
  }
};

export const getRankingRecords = async (period: RankingPeriod): Promise<RankingRecord[]> => {
  try {
    const dateRange = getDateRangeForPeriod(period);
    
    const q = query(
      collection(db, 'rankings'),
      where('date', '>=', dateRange.start),
      where('date', '<=', dateRange.end),
      where('isExcluded', '==', false),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as RankingRecord));
  } catch (error) {
    console.error('ランキング記録取得エラー:', error);
    return [];
  }
};

export const getMyRankingRecords = async (): Promise<RankingRecord[]> => {
  try {
    const userId = getUserId();
    
    const q = query(
      collection(db, 'rankings'),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as RankingRecord));
  } catch (error) {
    console.error('自分のランキング記録取得エラー:', error);
    return [];
  }
};

export const deleteRankingRecord = async (recordId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'rankings', recordId));
  } catch (error) {
    console.error('ランキング記録削除エラー:', error);
    throw error;
  }
};

// ========== 投票機能 ==========

export const voteRecord = async (recordId: string, voteType: 'good' | 'bad'): Promise<void> => {
  try {
    const userId = getUserId();
    
    // 既存の投票をチェック
    const votesQuery = query(
      collection(db, 'votes'),
      where('userId', '==', userId),
      where('recordId', '==', recordId)
    );
    
    const votesSnapshot = await getDocs(votesQuery);
    
    // 既に投票済みの場合は何もしない
    if (!votesSnapshot.empty) {
      throw new Error('既に投票済みです');
    }
    
    // 投票を記録
    const voteData: Omit<Vote, 'id'> = {
      userId,
      recordId,
      type: voteType,
      createdAt: Date.now()
    };
    
    await addDoc(collection(db, 'votes'), voteData);
    
    // レコードのカウントを更新
    const recordRef = doc(db, 'rankings', recordId);
    const updateField = voteType === 'good' ? 'goodCount' : 'badCount';
    await updateDoc(recordRef, {
      [updateField]: increment(1)
    });
    
    // バッドが閾値を超えた場合は除外フラグを立てる
    if (voteType === 'bad') {
      const recordDoc = await getDoc(recordRef);
      if (recordDoc.exists()) {
        const record = recordDoc.data() as RankingRecord;
        if (record.badCount + 1 >= BAD_THRESHOLD) {
          await updateDoc(recordRef, { isExcluded: true });
        }
      }
    }
  } catch (error) {
    console.error('投票エラー:', error);
    throw error;
  }
};

export const hasVoted = async (recordId: string): Promise<{ voted: boolean; type?: 'good' | 'bad' }> => {
  try {
    const userId = getUserId();
    
    const q = query(
      collection(db, 'votes'),
      where('userId', '==', userId),
      where('recordId', '==', recordId)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return { voted: false };
    }
    
    const vote = snapshot.docs[0].data() as Vote;
    return { voted: true, type: vote.type };
  } catch (error) {
    console.error('投票確認エラー:', error);
    return { voted: false };
  }
};

// ========== ヘルパー関数 ==========

const getDateRangeForPeriod = (period: RankingPeriod): { start: string; end: string } => {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  
  switch (period) {
    case 'today':
      return { start: todayStr, end: todayStr };
    
    case 'week': {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return {
        start: startOfWeek.toISOString().split('T')[0],
        end: todayStr
      };
    }
    
    case 'month': {
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
      return {
        start: monthStart,
        end: todayStr
      };
    }
    default:
      return { start: todayStr, end: todayStr };
  }
};
